import {
  createSignal,
  createMemo,
  createEffect,
  onCleanup,
  For,
  Show,
  type JSX,
} from 'solid-js';
import type { PortfolioSymptom } from '@lib/supabase/portfolio';
import type { Symptom } from '@lib/supabase/symptoms';

// Relational symptom editor — picker over the patient_symptoms
// controlled vocabulary (migration 0036). Originally inlined in
// PortfolioRoute; extracted here so the Watchlist (and any future
// surface) can reuse the exact same UX without copy-paste drift.
//
// The editor is intentionally agnostic about *where* the symptoms
// attach — the parent supplies onChange (persist a new id list) and
// onSuggest (create a pending row + attach it). Every surface now writes
// through setStrainSymptoms (user_strain_symptoms, migration 0049), so
// the tag follows the strain across /strains, /stock, /watchlist and
// /my-strains.
//
// Three concerns the previous free-text editor didn't have to handle:
//
//   1. Selected chips show a "pending" badge when the underlying
//      symptom hasn't been admin-approved. Pending names are private
//      to the submitter (per RLS), so the badge is the user's own
//      visibility cue — not a public signal.
//
//   2. Picking writes IDs, not names. We dedupe against the user's
//      current selection by id; the parent's onChange persists the
//      new set via setStrainSymptoms which diffs add/remove
//      server-side.
//
//   3. "Suggest new" creates a pending row + immediately attaches it
//      via the parent's onSuggest callback. The parent typically
//      refetches the catalog so the new entry shows up in other
//      entries' pickers without a full reload.

export default function RelationalSymptomEditor(props: {
  current: PortfolioSymptom[];
  catalog: Symptom[];
  onChange: (symptomIds: string[]) => Promise<void>;
  onSuggest: (name: string, currentIds: string[]) => Promise<Symptom>;
}): JSX.Element {
  const [open, setOpen] = createSignal(false);
  const [query, setQuery] = createSignal('');
  const [busy, setBusy] = createSignal(false);
  let dropdownRef: HTMLDivElement | undefined;

  function close() {
    setQuery('');
    setOpen(false);
  }

  // Close the dropdown when the user clicks anywhere outside it. The
  // listener is only attached while the dropdown is open so we don't
  // pay the cost on every page click. `mousedown` fires before `click`
  // so it dismisses cleanly even when the user is starting a drag.
  createEffect(() => {
    if (!open()) return;
    function onDocPointer(e: MouseEvent) {
      if (!dropdownRef) return;
      if (dropdownRef.contains(e.target as Node)) return;
      close();
    }
    document.addEventListener('mousedown', onDocPointer);
    onCleanup(() => document.removeEventListener('mousedown', onDocPointer));
  });

  const currentIds = createMemo(() => props.current.map((s) => s.id));

  // Picker options = catalog minus already-selected. We also exclude
  // pending submissions from other users (the RLS layer already blocks
  // these from reaching the client, but a defensive filter here keeps
  // the UI deterministic if a future query ever widens visibility).
  const options = createMemo(() => {
    const selected = new Set(currentIds());
    const q = query().trim().toLowerCase();
    return props.catalog
      .filter((s) => !selected.has(s.id))
      .filter((s) => (q ? s.name.toLowerCase().includes(q) : true))
      .sort((a, b) => {
        // Approved before pending in the picker, then alphabetical.
        if (a.status !== b.status) return a.status === 'approved' ? -1 : 1;
        return a.name.localeCompare(b.name);
      });
  });

  // "Suggest new" only surfaces when no exact match exists in the full
  // catalog (case-insensitive). Otherwise the user should pick the
  // existing row rather than creating a duplicate that an admin would
  // immediately have to merge.
  const canSuggest = createMemo(() => {
    const q = query().trim();
    if (!q) return false;
    const lower = q.toLowerCase();
    return !props.catalog.some((s) => s.name.toLowerCase() === lower);
  });

  async function add(symptomId: string) {
    setBusy(true);
    try {
      await props.onChange([...currentIds(), symptomId]);
      setQuery('');
      setOpen(false);
    } finally {
      setBusy(false);
    }
  }

  async function remove(symptomId: string) {
    setBusy(true);
    try {
      await props.onChange(currentIds().filter((id) => id !== symptomId));
    } finally {
      setBusy(false);
    }
  }

  async function suggest() {
    const name = query().trim();
    if (!name) return;
    setBusy(true);
    try {
      await props.onSuggest(name, currentIds());
      setQuery('');
      setOpen(false);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div class="flex flex-wrap items-start gap-1.5">
      <For each={props.current}>
        {(s) => (
          <span
            class={`text-[10px] rounded-full pl-2 pr-1 py-0.5 inline-flex items-center gap-1 ${
              s.status === 'approved'
                ? 'bg-brand-hex text-brand-green'
                : 'bg-amber-100 text-amber-800'
            }`}
            title={s.status === 'pending' ? 'Awaiting admin approval' : undefined}
          >
            {s.name}
            <Show when={s.status === 'pending'}>
              <span class="text-[8px] uppercase opacity-70">pending</span>
            </Show>
            <button
              type="button"
              onClick={() => remove(s.id)}
              disabled={busy()}
              class="opacity-60 hover:opacity-100"
              aria-label={`Remove ${s.name}`}
            >
              ×
            </button>
          </span>
        )}
      </For>

      <Show
        when={open()}
        fallback={
          <button
            type="button"
            onClick={() => setOpen(true)}
            class="text-[10px] rounded-full border border-dashed border-brand-hex text-brand-green/80 px-2 py-0.5 hover:bg-brand-hex"
          >
            + Symptom
          </button>
        }
      >
        <div
          ref={dropdownRef}
          class="rounded-md border border-brand-hex bg-white p-2 w-full sm:w-64 shadow-sm"
        >
          <div class="flex items-center gap-1">
            <input
              type="text" name="text"
              value={query()}
              // Ref callback fires when the element mounts in the DOM,
              // which (because the input lives inside <Show when={open()}>)
              // is exactly the moment the user clicked "+ Symptom". The
              // bare `autofocus` HTML attribute alone doesn't fire on
              // dynamic mounts — it only triggers on initial page load —
              // so without this the user has to click the field after
              // clicking the button. queueMicrotask defers the focus
              // call past Solid's render commit so the input is fully
              // wired up before we call .focus().
              ref={(el) => queueMicrotask(() => el.focus())}
              onInput={(e) => setQuery(e.currentTarget.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') close();
                if (e.key === 'Enter') {
                  e.preventDefault();
                  const first = options()[0];
                  if (first) void add(first.id);
                  else if (canSuggest()) void suggest();
                }
              }}
              placeholder="Search symptoms…"
              disabled={busy()}
              class="flex-1 min-w-0 rounded-md border border-brand-hex px-2 py-1 text-xs"
            />
            <button
              type="button"
              onClick={close}
              aria-label="Close symptom picker"
              title="Close"
              class="shrink-0 rounded-md p-1 text-brand-green/80 hover:bg-brand-hex hover:text-brand-green focus:outline-none focus:ring-2 focus:ring-brand-moss"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="w-4 h-4">
                <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
              </svg>
            </button>
          </div>
          <ul class="mt-1 max-h-40 overflow-y-auto">
            <For each={options().slice(0, 8)}>
              {(s) => (
                <li>
                  <button
                    type="button"
                    onClick={() => void add(s.id)}
                    disabled={busy()}
                    class="w-full text-left text-xs rounded-md px-2 py-1 hover:bg-brand-hex/40 flex items-center justify-between gap-2"
                  >
                    <span>{s.name}</span>
                    <Show when={s.status === 'pending'}>
                      <span class="text-[9px] uppercase rounded-full bg-amber-100 text-amber-800 px-1.5 py-0.5">
                        pending
                      </span>
                    </Show>
                  </button>
                </li>
              )}
            </For>
            <Show when={options().length === 0 && !canSuggest()}>
              <li class="text-xs text-brand-green/60 px-2 py-1">No matches.</li>
            </Show>
          </ul>
          <Show when={canSuggest()}>
            <button
              type="button"
              onClick={() => void suggest()}
              disabled={busy()}
              class="mt-1 w-full text-left text-xs rounded-md px-2 py-1 hover:bg-brand-moss/10 text-brand-moss"
            >
              + Suggest "{query().trim()}" (pending review)
            </button>
          </Show>
        </div>
      </Show>
    </div>
  );
}
